// Settings page — offline storage, units, map style, about
import { useState, useEffect } from 'react';
import { HardDrive, Map, Ruler, Info, Trash2, RefreshCw } from 'lucide-react';
import { formatBytes } from '@/lib/opfs-tiles';
import { useSyncState } from '@/components/SyncProvider';
import { getPowerSyncDB } from '@/lib/sync';

export function SettingsPage() {
  const [storageUsed, setStorageUsed] = useState(0);
  const [storageQuota, setStorageQuota] = useState(0);
  const [units, setUnits] = useState<'imperial' | 'metric'>(
    (localStorage.getItem('bluguide_units') as any) ?? 'imperial'
  );
  const { connected, lastSyncedAt, hasSynced } = useSyncState();
  const [forceSyncing, setForceSyncing] = useState(false);

  const handleForcSync = async () => {
    setForceSyncing(true);
    try {
      // Trigger an immediate upload attempt — PowerSync queues the rest
      const db = getPowerSyncDB();
      await db.waitForFirstSync?.();
    } catch { /* ignore */ } finally {
      setForceSyncing(false);
    }
  };

  useEffect(() => {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then(est => {
        setStorageUsed(est.usage ?? 0);
        setStorageQuota(est.quota ?? 0);
      });
    }
  }, []);

  const toggleUnits = () => {
    const next = units === 'imperial' ? 'metric' : 'imperial';
    setUnits(next);
    localStorage.setItem('bluguide_units', next);
  };

  const clearLocalData = async () => {
    if (!confirm('Clear all local data? This cannot be undone.')) return;
    // Clear IndexedDB
    indexedDB.deleteDatabase('bluguide');
    // Clear OPFS
    const root = await navigator.storage.getDirectory();
    for await (const [name] of (root as any).entries()) {
      await root.removeEntry(name, { recursive: true });
    }
    location.reload();
  };

  const pct = storageQuota > 0 ? Math.round((storageUsed / storageQuota) * 100) : 0;

  return (
    <div className="flex flex-col h-full bg-[#000] text-white overflow-y-auto pb-safe">
      <div className="px-5 pt-14 pb-4 border-b border-[#2c2c2e]">
        <h1 className="text-2xl font-bold">Settings</h1>
      </div>

      <div className="px-4 py-4 space-y-6">
        {/* Storage */}
        <Section icon={<HardDrive className="w-5 h-5" />} title="Storage">
          <div className="space-y-3">
            <div className="h-2.5 bg-[#2c2c2e] rounded-full overflow-hidden">
              <div className="h-full bg-[#1a4a2e] rounded-full" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-[#8e8e93] text-sm">
              {formatBytes(storageUsed)} used of {formatBytes(storageQuota)}
            </p>
          </div>
        </Section>

        {/* Sync */}
        <Section icon={<RefreshCw className="w-5 h-5" />} title="Sync">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white text-sm">{connected ? '● Connected' : '○ Local only'}</p>
              <p className="text-[#8e8e93] text-xs mt-0.5">
                {lastSyncedAt ? `Last synced ${lastSyncedAt.toLocaleTimeString()}` : 'Not yet synced'}
              </p>
            </div>
            <button
              onClick={handleForcSync}
              disabled={forceSyncing || !connected}
              className="px-3 py-1.5 rounded-full bg-[#1a4a2e] text-white text-xs font-medium disabled:opacity-40"
            >
              {forceSyncing ? 'Syncing…' : 'Sync Now'}
            </button>
          </div>
        </Section>

        {/* Units */}
        <Section icon={<Ruler className="w-5 h-5" />} title="Units">
          <div className="flex items-center justify-between">
            <span className="text-white text-sm">
              {units === 'imperial' ? 'Imperial (lb, in, °F)' : 'Metric (kg, cm, °C)'}
            </span>
            <button
              onClick={toggleUnits}
              className="px-4 py-1.5 rounded-full bg-[#1a4a2e] text-white text-sm font-medium"
            >
              Switch
            </button>
          </div>
        </Section>

        {/* About */}
        <Section icon={<Info className="w-5 h-5" />} title="About">
          <div className="space-y-1 text-[#8e8e93] text-sm">
            <p>BluGuide Maine · v0.1.0 MVP</p>
            <p>Offline-first fly fishing companion</p>
            <p className="text-xs mt-2">
              Map data © OpenStreetMap contributors · MapLibre GL JS · PMTiles · OpenFreeMap
            </p>
          </div>
        </Section>

        {/* Danger zone */}
        <Section icon={<Trash2 className="w-5 h-5 text-[#d45a1a]" />} title="Danger Zone">
          <button
            onClick={clearLocalData}
            className="w-full py-3 rounded-xl bg-[#d45a1a]/20 text-[#d45a1a] text-sm font-semibold"
          >
            Clear All Local Data
          </button>
        </Section>
      </div>
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-[#1c1c1e] rounded-2xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[#8e8e93]">{icon}</span>
        <h2 className="text-white font-semibold text-sm">{title}</h2>
      </div>
      {children}
    </div>
  );
}
